/**
 * CountryContext — React context for country-specific state.
 *
 * Wraps the country preference store and provides a React-friendly API
 * for components to read and update the current country.
 *
 * The spine (shell) mounts <CountryProvider> near the root. All plugin apps
 * receive country context via AppHostApi.locale, not through this React
 * context directly (to avoid cross-bundle context sharing issues).
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { SupportedCountry, CountryContext, FirstDayOfWeekPreference } from './country';
import {
  buildCountryContext,
  setStoredCountry,
  setStoredFirstDayOfWeek,
  getStoredFirstDayOfWeek,
  getFirstDayOfWeekPreference,
  getCurrentCountryContext,
  COUNTRY_CHANGE_EVENT,
} from './country';

// ── Context ───────────────────────────────────────────────────────────────────

interface CountryContextValue {
  /** Full country context: metadata + feature flags */
  countryCtx: CountryContext;
  /** Change the active country and persist to localStorage */
  setCountry: (code: SupportedCountry) => void;
  /** Explicit first day of week preference ('auto', 0, 1) */
  firstDayOfWeekPreference: FirstDayOfWeekPreference;
  /** Change the first day of week preference */
  setFirstDayOfWeek: (pref: FirstDayOfWeekPreference) => void;
}

const CountryReactContext = createContext<CountryContextValue>({
  countryCtx: getCurrentCountryContext(),
  setCountry: () => {},
  firstDayOfWeekPreference: 'auto',
  setFirstDayOfWeek: () => {},
});

// ── Provider ──────────────────────────────────────────────────────────────────

export const CountryProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [countryCtx, setCountryCtx] = useState<CountryContext>(
    () => getCurrentCountryContext(),
  );
  const [pref, setPref] = useState<FirstDayOfWeekPreference>(
    () => getFirstDayOfWeekPreference(),
  );

  const setCountry = useCallback((code: SupportedCountry) => {
    const ctx = setStoredCountry(code);
    setCountryCtx(ctx);
  }, []);

  const setFirstDayOfWeek = useCallback((p: FirstDayOfWeekPreference) => {
    const ctx = setStoredFirstDayOfWeek(p);
    setPref(p);
    setCountryCtx(ctx);
  }, []);

  useEffect(() => {
    const onCountryChange = () => {
      setCountryCtx(getCurrentCountryContext());
      setPref(getFirstDayOfWeekPreference());
    };
    window.addEventListener(COUNTRY_CHANGE_EVENT, onCountryChange);
    return () => window.removeEventListener(COUNTRY_CHANGE_EVENT, onCountryChange);
  }, []);

  return (
    <CountryReactContext.Provider value={{ countryCtx, setCountry, firstDayOfWeekPreference: pref, setFirstDayOfWeek }}>
      {children}
    </CountryReactContext.Provider>
  );
};

// ── Hooks ─────────────────────────────────────────────────────────────────────

/**
 * Access the current country context (metadata + feature flags) and preference setters.
 */
export const useCountry = () => useContext(CountryReactContext);

/**
 * Convenience hook: returns only the feature flags for the current country.
 */
export const useCountryFeatures = () =>
  useContext(CountryReactContext).countryCtx.features;

/**
 * Convenience hook: returns only the CountryOption metadata.
 */
export const useCountryOption = () =>
  useContext(CountryReactContext).countryCtx.country;

/**
 * Convenience hook: returns first day of the week (0=Sunday, 1=Monday) and setter.
 */
export const useFirstDayOfWeek = () => {
  const { countryCtx, firstDayOfWeekPreference, setFirstDayOfWeek } = useContext(CountryReactContext);
  return {
    firstDayOfWeek: countryCtx.features.firstDayOfWeek,
    preference: firstDayOfWeekPreference,
    setFirstDayOfWeek,
  };
};

/**
 * Build a snapshot of the current CountryContext without React.
 * Useful in non-React code (e.g., CustomRuntime.createHost).
 */
export { getCurrentCountryContext, buildCountryContext, getStoredFirstDayOfWeek, setStoredFirstDayOfWeek, getFirstDayOfWeekPreference };
